import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, MapPin, Phone, Clock, Star, Truck, CalendarCheck, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import AppLayout from "@/components/app/AppLayout";
import { restApi } from "@/lib/restApi";

export default function RestaurantDetail() {
  const { rid } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);

  useEffect(() => {
    restApi.detail(rid).then(setItem).catch(() => toast.error("Restaurante no encontrado"));
  }, [rid]);

  if (!item) return <AppLayout hideRightRail><p className="text-center py-12 text-ink-mid">Cargando…</p></AppLayout>;

  const directionsUrl = item.lat && item.lng
    ? `https://www.google.com/maps/dir/?api=1&destination=${item.lat},${item.lng}`
    : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(item.name + ", " + (item.address || item.city))}`;

  return (
    <AppLayout hideRightRail>
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-xs text-ink-mid hover:text-coral mb-4" data-testid="rest-detail-back">
        <ArrowLeft className="w-3.5 h-3.5" /> Volver
      </button>

      <div className="grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-7">
          <div className="aspect-[4/3] rounded-2xl overflow-hidden bg-cream-deep">
            {item.photos?.[0]
              ? <img src={item.photos[0]} alt={item.name} className="w-full h-full object-cover" />
              : <div className="w-full h-full flex items-center justify-center text-ink-mid">Sin foto</div>}
          </div>
        </div>

        <div className="lg:col-span-5">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-coral mb-1">
            {item.cuisine} · {item.price_range} · {item.zone || item.city}
          </p>
          <h1 data-testid="rest-detail-title" className="font-serif text-3xl md:text-4xl tracking-tight leading-tight mb-2">{item.name}</h1>

          <div className="flex items-center gap-3 text-sm mb-4">
            <span className="inline-flex items-center gap-1 font-medium">
              <Star className="w-4 h-4 fill-coral text-coral" /> {item.rating?.toFixed(1)}
            </span>
            <span className="text-ink-mid">· {item.reviews_count} reseñas</span>
          </div>

          {item.description && <p className="text-sm leading-relaxed mb-4">{item.description}</p>}

          <div className="space-y-2 text-sm mb-5">
            {item.address && (
              <p className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 text-coral shrink-0" />
                <span>{item.address}{item.city ? `, ${item.city}` : ""}{item.country ? `, ${item.country}` : ""}</span>
              </p>
            )}
            {item.phone && (
              <p className="flex items-center gap-2"><Phone className="w-4 h-4 text-coral" />
                <a href={`tel:${item.phone}`} className="hover:text-coral">{item.phone}</a>
              </p>
            )}
            {item.hours && (
              <p className="flex items-center gap-2"><Clock className="w-4 h-4 text-coral" /> {item.hours}</p>
            )}
          </div>

          <div className="flex flex-wrap gap-2 mb-5">
            {item.has_delivery && (
              <span className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-700">
                <Truck className="w-3.5 h-3.5" /> Delivery
              </span>
            )}
            {item.has_reservations && (
              <span className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-blue-100 text-blue-700">
                <CalendarCheck className="w-3.5 h-3.5" /> Acepta reservas
              </span>
            )}
            {item.is_open_now && (
              <span className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-cream-card border hairline">
                Abierto ahora
              </span>
            )}
          </div>

          <a href={directionsUrl} target="_blank" rel="noreferrer" data-testid="rest-detail-directions"
             className="w-full inline-flex items-center justify-center gap-2 bg-coral text-white rounded-full py-3 text-sm hover:bg-coral-hover transition">
            <ExternalLink className="w-4 h-4" /> Cómo llegar
          </a>
          {item.menu_url && (
            <a href={item.menu_url} target="_blank" rel="noreferrer"
               className="mt-2 w-full inline-flex items-center justify-center gap-2 border hairline rounded-full py-2.5 text-sm hover:border-coral transition">
              Ver menú
            </a>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
